import type { LucideIcon } from 'lucide-react';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { Section } from '@/components/Section';
import { Skeleton } from '@/components/Skeleton';
import { formatNumber } from '@/utils';
import * as styles from './TopCategory.css';

type TopCategoryProps = {
    /**
     * The icon to display inside the circle on the left. This should be a Lucide icon component.
     */
    icon: LucideIcon;
    /**
     * The title to display above the value (e.g., '最高評価カテゴリー').
     */
    title: string;
    /**
     * The category to display, or `null` while it is still loading. Shaped to take a
     * `SurveyOverallScores` `highestCategory`/`mostImprovedCategory` as-is.
     *
     * Grouped rather than passed as separate props because each field is itself
     * nullable when a value can't be computed — a loaded state that renders as 'N/A',
     * which the individual nulls alone couldn't be told apart from.
     */
    category: {
        /**
         * The name of the category. If null, 'N/A' will be shown instead.
         */
        name: string | null;
        /**
         * The DI value for the category. If null, 'N/A' will be shown instead.
         */
        di: number | null;
        /**
         * The deviation value for the category. If null, 'N/A' will be shown instead.
         */
        deviation: number | null;
    } | null;
    /** Opt-in: omit when this data has no independent failure mode to report. */
    isError?: boolean;
    onRetry?: () => void;
};

export function TopCategory({ icon, title, category, isError, onRetry }: TopCategoryProps) {
    if (isError) return <ErrorOverlay active onRetry={onRetry} message={`${title}の取得に失敗しました。`} />;

    if (!category) return <TopCategorySkeleton />;

    return <TopCategoryContent icon={icon} title={title} {...category} />;
}

type TopCategoryContentProps = Omit<TopCategoryProps, 'category' | 'isError' | 'onRetry'> &
    NonNullable<TopCategoryProps['category']>;

// Split from the buffer above so its rendering stays free of loading concerns.
function TopCategoryContent({ icon, title, name, di, deviation }: TopCategoryContentProps) {
    return (
        <Section pt="8" pb="8" flex>
            <Flex gap="8" align="center">
                <div className={styles.iconCircle}>
                    <Icon icon={icon} size="lg" className={styles.icon} />
                </div>

                <Flex direction="column" gap="2">
                    <div className={styles.title}>{title}</div>

                    <div className={styles.categoryName}>{name !== null ? name : 'N/A'}</div>

                    <div className={styles.subtext}>
                        <span>
                            DI {di !== null ? <span className={styles.scoreValue}>{formatNumber(di)}</span> : 'N/A'}
                        </span>{' '}
                        <span>
                            偏差値{' '}
                            {deviation !== null ? (
                                <span className={styles.scoreValue}>{formatNumber(deviation)}</span>
                            ) : (
                                'N/A'
                            )}
                        </span>
                    </div>
                </Flex>
            </Flex>
        </Section>
    );
}

export function TopCategorySkeleton() {
    return (
        <Section pt="8" pb="8" flex>
            <Flex gap="8" align="center">
                <Skeleton.Circle radius={56} />

                <Flex direction="column" gap="4" flex>
                    <Skeleton.Line width="40%" height="sm" />
                    <Skeleton.Line width="70%" height="4xl" />
                    <Skeleton.Line width="50%" height="xs" />
                </Flex>
            </Flex>
        </Section>
    );
}
